<template>
  <div
    tabindex="0"
    :title="(clickAction !== 'disabled') ? productName : null"
    :class="['product-description', { 'text-center': descriptionAlignment === 'center' }]"
    @keyup.enter="triggerPdpLink"
  >
    <div
      v-if="product?.detail?.flags?.sponsoredItemTop && relatedAlignment === 'horizontal'"
      class="sponsored-item-top-label"
    >
      <span>
        Sponsored
      </span>
    </div>
    <ColorSwatches
      v-if="hasSwatchlet && !storeOnly?.inStoreOnly && !isBcom && !colorSwatchEnhancement"
      class="color-swatch"
      :traits="product?.traits"
      :url-template="swatchUrlTemplate"
      :product-url="product?.identifier?.productUrl"
      :swatches-alignment="descriptionAlignment"
      :open-in-new-tab-remaining-colors="openInNewTab"
      @color="selectedColorId"
      @remainingColors="remainingColors"
      @isHovering="isHovering"
      @colorSwatch="colorSwatch"
    />
    <!-- TODO: add color to url if needed -->
    <div
      :class="{'description-width': alignPricingBlockToRight }"
    >
      <div class="description-spacing">
        <component
          :is="componentType"
          ref="productNameLink"
          :title="(clickAction !== 'disabled') ? productName : null"
          :href="(clickAction !== 'disabled') ? url : null"
          :aria-labelledby="(clickAction !== 'disabled') ? productName : null"
          :class="[
            'brand-and-name',
            { 'clickAction': clickAction === 'fireEvent', 'without-text-decoration': isHorizontalLeftWithRightPriceAlignment }
          ]"
          v-bind="extraAttributes"
          @click="fireEvent"
        >
          <div
            v-if="brandName"
            :class="['product-brand', brandClass, { 'margin-bottom-xxs': isHorizontalLeftWithRightPriceAlignment }]"
          >
            <div
              v-if="isNewBcom"
              class="heavy-sm"
            >
              NEW!
            </div>
            {{ brandName }}
          </div>
          <div
            v-if="productName && isSortableGrid"
          >
            <h3 :class="[{ 'product-name': !wrapProductName }, { 'medium': !isBcom }]">
              <strong v-if="isNewMcom">NEW!</strong>
              {{ productName }}
            </h3>
          </div>
          <div
            v-else-if="productName"
            :class="[{ 'product-name': !wrapProductName }, { 'medium': !isBcom }]"
          >
            <strong v-if="isNewMcom">NEW!</strong>
            {{ productName }}
          </div>
        </component>
        <div
          v-if="alignPricingBlockToRight"
          class="selected-traits margin-top-xs"
        >
          <span
            v-if="product?.traits?.sizes"
            class="product-size-color"
          >
            {{ product?.traits?.sizes[0] }}
          </span>
          <span v-if="product?.traits?.sizes && (product?.traits?.types || product?.traits?.colors)">, </span>
          <span
            v-if="product?.traits?.types"
            class="product-size-color"
          >
            {{ product?.traits?.types[0] }}
          </span>
          <span v-if="product?.traits?.types && product?.traits?.colors">, </span>
          <span
            v-if="product?.traits?.colors"
            class="product-size-color"
          >
            {{ product?.traits?.colors[0] }}
          </span>
        </div>
      </div>
    </div>
    <div
      v-if="!storeOnly?.inStoreOnly"
      :class="[{ 'block-alignment': !storeOnly?.inStoreOnly && alignPricingBlockToRight }, sectionSpacing, 'pricing-and-reviewlet']"
    >
      <div v-if="marketPlaceSellerName">
        Shipped and sold by {{ marketPlaceSellerName }}
      </div>
      <div
        v-if="product?.detail?.colorAndSize?.length"
        class="product-size-color"
      >
        <div
          v-for="item in product?.detail?.colorAndSize"
          :key="item"
        >
          {{ item }}
        </div>
      </div>
      <div
        v-if="product?.detail?.quantity && product?.detail?.quantity > 1"
        class="product-qty"
      >
        <span>Qty: {{ product?.detail?.quantity }}</span>
      </div>
      <div
        v-if="product?.detail?.giftCardEmail"
        class="margin-top-xs"
      >
        {{ product?.detail?.giftCardEmail }}
      </div>
      <template v-if="isGwpProduct">
        <span class="price-reg"> FREE GIFT </span>
      </template>
      <div
        v-else-if="alignPricingBlockToRight"
        class="price-alignment-to-right price-reg"
      >
        <template
          v-if="!product?.detail?.flags?.gwpIndicator"
        >
          <PricingBlockSimplification
            v-if="isSimplificationOn"
            :pricing="product?.pricing?.price"
            :simplification-with-v1="simplificationWithV1"
            :simplification-non-bold-percentage-exp="simplificationNonBoldPercentageExp"
            :simplification-remove-sale-exp="simplificationRemoveSaleExp"
            :remove-tooltip-on-fgwp-badge-enabled="removeTooltipOnFgwpBadgeEnabled"
            :suppress-tool-tip="suppressToolTip"
            class="no-margin-top price-simplification-gwp"
          />
          <PricingBlock
            v-else
            :pricing="product?.pricing?.price"
            class="no-margin-top"
          />
        </template>

        <div v-else-if="product?.detail?.flags?.gwpIndicator">
          <span class="price-reg">Free</span>
        </div>
      </div>
      <template v-else>
        <PricingBlockSimplification
          v-if="isSimplificationOn"
          :pricing="product?.pricing?.price"
          :is-final-price="product?.pricing?.badges
            ? product?.pricing?.badges[1]?.secondBadgeWithFinalPrice
            : false
          "
          :show-price-type-above-price="showPriceTypeAbovePrice"
          :display-sale-price-only="displaySalePriceOnly"
          :override-price-simplification="overridePriceSimplification"
          :badges-data="badgesData"
          :simplification-with-v1="simplificationWithV1"
          :simplification-non-bold-percentage-exp="simplificationNonBoldPercentageExp"
          :simplification-remove-sale-exp="simplificationRemoveSaleExp"
          :loyalty-badge="loyaltyBadge"
          :remove-tooltip-on-fgwp-badge-enabled="removeTooltipOnFgwpBadgeEnabled"
          :suppress-tool-tip="suppressToolTip"
          :class="cssMarginSimplification"
        />
        <PricingBlock
          v-else
          :pricing="product?.pricing?.price"
          :is-final-price="product?.pricing?.badges
            ? product?.pricing?.badges[1]?.secondBadgeWithFinalPrice
            : false
          "
          :show-price-type-above-price="showPriceTypeAbovePrice"
          :display-sale-price-only="displaySalePriceOnly"
          :class="cssMarginSimplification"
        />
      </template>
      <template v-if="!isSimplificationOn && (product?.pricing?.badges && !isBcom)">
        <Badges
          :badges="product?.pricing?.badges"
          :suppress-tool-tip="suppressToolTip"
          :loyalty-badge="loyaltyBadge"
          :remove-tooltip-on-fgwp-badge-enabled="removeTooltipOnFgwpBadgeEnabled"
        />
      </template>
      <div
        v-if="product?.returnWindowDate"
        class="product-return-window"
      >
        <span>Return by {{ product?.returnWindowDate }}</span>
      </div>
      <div
        v-if="productReviews"
        :class="[{ 'rating-align': descriptionAlignment === 'center' }, 'reviewlet-spacing', cssMarginSimplification]"
      >
        <ReviewCondensed
          v-if="showReviewCondensed"
          :review-count="productReviews?.count"
          :rating="productReviews?.rating"
          :review-link="reviewLink"
        />

        <Reviewlet
          v-else
          size="small"
          :review-count="productReviews?.count"
          :rating="productReviews?.rating"
          :review-link="reviewLink"
          :show-review-text="!hideReviewTextAndRating"
          :show-rating="!hideReviewTextAndRating"
        />
      </div>
    </div>
    <ColorSwatches
      v-if="hasSwatchlet && !storeOnly?.inStoreOnly && (isBcom || (!isBcom && colorSwatchEnhancement))"
      class="color-swatch"
      :class="{'color-swatch-exp':colorSwatchEnhancement}"
      :traits="product?.traits"
      :url-template="swatchUrlTemplate"
      :product-url="product?.identifier?.productUrl"
      :swatches-alignment="descriptionAlignment"
      :open-in-new-tab-remaining-colors="openInNewTab"
      :color-swatch-enhancement="colorSwatchEnhancement"
      :device-type="deviceType"
      @color="selectedColorId"
      @remainingColors="remainingColors"
      @isHovering="isHovering"
      @colorSwatch="colorSwatch"
    />
    <div
      v-if="!storeOnly?.inStoreOnly || product?.pricing?.badges"
      :class="[sectionSpacing, 'shipping-and-mlt']"
    >
      <div
        v-if="shippingType"
        class="shipping-options"
      >
        <ShippingOptions
          :flags="product?.detail?.flags"
          :shipping-type="shippingType"
        />
      </div>
      <template v-if="!isSimplificationOn && product?.pricing?.badges && isBcom">
        <Badges
          :badges="product?.pricing?.badges"
          :is-simplification-on="isSimplificationOn"
          :override-price-simplification="overridePriceSimplification"
        />
      </template>
      <div
        v-if="showMoreLikeThisBtn"
        :id="`mlt-${product?.id}`"
        class="similar-products-feature small more-like-this-btn"
        :class="{ 'align-center': descriptionAlignment === 'center' }"
        tabindex="0"
        @click="moreLikeThis(`mlt-${product?.id}`)"
        @keydown="event => toggleMoreLikeThis(`mlt-${product?.id}`, event)"
      >
        More Like This
        <span :class="[expanded[`mlt-${product?.id}`] ? 'arrow-up' : 'arrow-down']" />
      </div>
    </div>

    <button
      v-if="showRemoveBtn"
      class="remove-button link-body margin-top-xs"
      @click="removeProduct"
    >
      Remove
    </button>
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import Reviewlet from '@atomic-ui/reviewlet';
import { decode } from '@vue-util/entities';
import TOGGLE from '../../store_modules/moreLikeThis/mutations';
import PricingBlock from './ProductDescription/PricingBlock';
import PricingBlockSimplification from './ProductDescription/PricingBlockSimplification';
import Badges from './ProductDescription/Badges';
import ShippingOptions from './ProductDescription/ShippingOptions';
import ColorSwatches from './ProductDescription/ColorSwatches';
import ReviewCondensed from './ProductDescription/ReviewCondensed';
import isBcom from '../../utils/isBcom';

export default {
  name: 'ProductDescription',

  components: {
    PricingBlock,
    PricingBlockSimplification,
    Reviewlet,
    Badges,
    ShippingOptions,
    ColorSwatches,
    ReviewCondensed,
  },
  props: {
    product: {
      type: Object,
      default: () => ({}),
      required: false,
    },
    showMoreLikeThisBtn: {
      type: Boolean,
      required: false,
      default: false,
    },
    showRemoveBtn: {
      type: Boolean,
      required: false,
      default: false,
    },
    shippingType: {
      type: Object,
      default: () => { },
    },
    clickAction: {
      type: String,
      default: 'linkToPdp',
    },
    swatchUrlTemplate: {
      type: String,
      default: '',
    },
    reviewLink: {
      required: false,
      type: String,
      default: null,
    },
    hideReviewTextAndRating: {
      type: Boolean,
      required: false,
      default: false,
    },
    alignPricingBlockToRight: {
      type: Boolean,
      required: false,
      default: false,
    },
    storeOnly: {
      type: Object,
      required: false,
      default: () => { },
    },
    showPriceTypeAbovePrice: {
      type: Boolean,
      required: false,
      default: false,
    },
    wrapProductName: {
      type: Boolean,
      required: false,
      default: false,
    },
    descriptionAlignment: {
      type: String,
      required: false,
      default: 'left',
    },
    relatedAlignment: {
      type: String,
      required: false,
      default: 'vertical',
    },
    brandClass: {
      type: String,
      required: false,
      default: () => (isBcom ? 'heavy' : 'medium'),
    },
    sectionSpacing: {
      type: String,
      required: false,
      default: 'expand',
    },
    displaySalePriceOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    openInNewTab: {
      type: Boolean,
      required: false,
      default: false,
    },
    url: {
      type: String,
      default: '',
    },
    isSimplificationOn: {
      type: Boolean,
      default: false,
    },
    simplificationWithV1: {
      type: Boolean,
      default: false,
    },
    simplificationNonBoldPercentageExp: {
      type: Boolean,
      default: false,
    },
    simplificationRemoveSaleExp: {
      type: Boolean,
      default: false,
    },
    overridePriceSimplification: {
      type: Boolean,
      default: false,
    },
    suppressToolTip: {
      type: Boolean,
      default: false,
    },
    isSortableGrid: {
      type: Boolean,
      default: false,
    },
    loyaltyBadge: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    showReviewCondensed: {
      type: Boolean,
      required: false,
    },
    removeTooltipOnFgwpBadgeEnabled: {
      type: Boolean,
      default: false,
    },
    colorSwatchEnhancement: {
      type: Boolean,
      required: false,
      default: false,
    },
    deviceType: {
      type: String,
      required: false,
      default: '',
    },
  },
  emits: ['setSelectedColor', 'getMoreDetails', 'moreLikeThis', 'removeProduct', 'isHovering', 'colorSwatch'],
  data() {
    return {
      isBcom,
    };
  },

  computed: {
    ...mapState('moreLikeThisStore', ['expanded']),
    productReviews() {
      return this.product?.detail?.reviewStatistics?.aggregate;
    },
    hasSwatchlet() {
      return this.product?.traits?.colors?.colorMap?.length > 1;
    },
    marketPlaceSellerName() {
      return this.product?.mkpLineItemDetails?.sellerName;
    },
    brandName() {
      return decode(this.product?.detail?.subBrand || this.product?.detail?.brand?.name || this.product?.detail?.brand || '');
    },
    productName() {
      const productDetailName = this.product?.detail?.productName || this.product?.detail?.name || '';
      return decode(productDetailName);
    },
    isHorizontalLeftWithRightPriceAlignment() {
      return this.descriptionAlignment === 'left' && this.relatedAlignment === 'horizontal' && this.alignPricingBlockToRight;
    },
    componentType() {
      return (this.clickAction !== 'disabled') && !this.storeOnly?.inStoreOnly ? 'a' : 'div';
    },
    extraAttributes() {
      if (this.componentType === 'a') {
        return this.openInNewTab ? { target: '_blank', rel: 'noopener', tabindex: '-1' } : { target: '_self', tabindex: '-1' };
      } return null;
    },
    isNewBcom() {
      return this.product?.detail?.flags?.isNew && isBcom;
    },
    isNewMcom() {
      return this.product?.detail?.flags?.isNew && !isBcom;
    },
    badgesData() {
      return this.product?.pricing?.badges ? this.product?.pricing?.badges : [];
    },
    cssMarginSimplification() {
      if (isBcom) {
        return this.isSimplificationOn && !this.overridePriceSimplification ? 'ps-margin-B' : 'ps-margin-A';
      }
      return this.isSimplificationOn && !this.overridePriceSimplification ? 'ps-margin-A' : 'ps-margin-B';
    },
    isGwpProduct() {
      return isBcom
        ? this.product?.detail?.typeName === 'GWP' && this.product?.detail?.flags?.giftProduct
        : this.product?.detail?.typeName === 'GWP' && this.product?.detail?.flags?.masterProduct !== true;
    },
  },
  methods: {
    ...mapMutations('moreLikeThisStore', {
      toggle: TOGGLE,
    }),
    selectedColorId(colorId) {
      this.$emit('setSelectedColor', colorId);
    },
    moreLikeThis(id) {
      this.toggle(id);
      this.$emit('moreLikeThis', this.product.id);
    },
    remainingColors(event) {
      this.$emit('getMoreDetails', event);
    },
    removeProduct() {
      this.$emit('removeProduct', this.product);
    },
    isHovering(value) {
      this.$emit('isHovering', value);
    },
    colorSwatch(value) {
      this.$emit('colorSwatch', value);
    },
    toggleMoreLikeThis(value, event) {
      if (event.key === 'Enter') {
        this.moreLikeThis(value);
        event.preventDefault();
      }
    },
    triggerPdpLink() {
      const pdpLinkExists = (this.clickAction !== 'disabled');
      if (pdpLinkExists && this.$refs.productNameLink) {
        this.$refs.productNameLink.click();
      }
    },
    fireEvent(event) {
      if (this.clickAction === 'fireEvent') {
        this.$emit('getMoreDetails', event);
        event.preventDefault();
        return false;
      }
      return true;
    },
  },
};
</script>

<style lang="scss" scoped>
.vertical-alignment {
  .product-description {
    .product-brand {
      margin-top: rem-calc(4);
    }
  }
}

.product-description {
  .expand {
    .pricing {
      &.ps-margin-A{
        margin-top: rem-calc(8);
      }
      &.ps-margin-B{
        margin-top: rem-calc(12);
      }
    }
  }

  a {
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }

  .without-text-decoration:hover {
    text-decoration: none;
  }

  .clickAction {
    cursor: pointer;
  }

  .reviewlet-spacing{
  .rating {
    :deep(fieldset) {
      display: flex;
      .review-star-small, .review-star-filled-small, .review-star-half-filled-small{
           margin-top: rem-calc(0);
      }
    }
    :deep(.rating-block-small){
      padding-top: rem-calc(0);
    }
  }
}
  .similar-products-feature {
    cursor: pointer;
    display: flex;
    align-items: center;
  }

  .collapse {
    .pricing {
      margin-top: rem-calc(4)
    }

    .shipping-options {
      &:not(:empty) {
        margin-top: rem-calc(4);
      }
    }

    .reviewlet-spacing {
      margin-top: rem-calc(4);
    }

    .similar-products-feature {
      margin-top: rem-calc(4);

    }
  }

  @include breakpoint(medium up) {
    .product-name {
      text-overflow: ellipsis;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }
  }

  @include breakpoint(small down) {
    .product-name {
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }

  .product-size-color {
    margin-top: rem-calc(10);
    color: $gray-1-color;
  }

  .selected-traits {
    .product-size-color {
      margin: 0;
    }
  }

  .product-qty,
  .product-return-window {
    color: $gray-1-color;
  }
  .rating-align :deep(.rating) {
  justify-content: center;
  }

}

.horizontal-alignment {
  .product-description {
    position: relative;
    overflow: hidden;

    .sponsored-item-top-label {
      margin-bottom: rem-calc(12px);
      span {
      font-size: rem-calc(10px);
      line-height: rem-calc(12px);
      color: $gray-1-color;
      }
    }
    .description-width {
      overflow-wrap: break-word;
      display: inline-block;

      .description-spacing {

        .product-brand,
        .product-name {
          white-space: normal;
        }
      }
    }

    .product-name {
      max-width: rem-calc(205);
      white-space: normal;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    @include breakpoint(medium) {
      .product-name {
        max-width: rem-calc(230);
      }
    }

    .block-alignment {
      display: inline;
    }
  }

  .remove-button {
    display: block;
  }
}
</style>

<style lang="scss" brand="mcom" scoped>
@import "@csgIcons/_chevron-small-down-black.scss";
@import "@csgIcons/_chevron-small-up-black.scss";

.price-alignment-to-right {
  position: absolute;
  top: 0;
  right: 0;
  text-align: right;

  @include breakpoint(small down) {
    display: block;
    margin-left: auto;
    margin-right: 0;
  }

  .sale {
    color: $sale-color;
  }
}

.product-description {

  .expand {
    .shipping-options {
      &:not(:empty) {
        margin-top: rem-calc(12);
      }
    }

    .reviewlet-spacing {
      &.ps-margin-A{
        margin-top: rem-calc(8);
      }
      &.ps-margin-B{
        margin-top: rem-calc(12);
      }
    }

    .similar-products-feature {
      margin-top: rem-calc(12);
      cursor: pointer;
      display: flex;
      align-items: center;
    }
  }

  .arrow-down {
    @extend .chevron-small-down-black;
  }

  .arrow-up {
    @extend .chevron-small-up-black;
  }
}
</style>

<style lang="scss" brand="bcom" scoped>
@import "@csgIcons/_chevron-small-down.scss";
@import "@csgIcons/_chevron-small-up.scss";

.product-description {
  display: flex;
  flex-direction: column;

  .expand {
    .shipping-options {
      &:not(:empty) {
        margin-top: rem-calc(8);
      }
    }

    .reviewlet-spacing {
      margin-top: rem-calc(8);
    }

    .similar-products-feature {
      margin-top: rem-calc(8);
      cursor: pointer;
      display: flex;
      align-items: center;
    }
  }
  .pricing-and-reviewlet{
    display: flex;
    flex-direction: column;
    order:2;
      :deep(.rating){
       order:1
      }
     :deep(.pricing) {
      order:2;
      }
  }
  .color-swatch{
    padding-top:rem-calc(4);
    order:3;
  }
  .color-swatch.color-swatch-exp{
    padding-top: 0;
  }
  .shipping-and-mlt{
    order:4
  }
  .arrow-down {
    @extend .chevron-small-down;
  }

  .arrow-up {
    @extend .chevron-small-up;
  }
}
</style>
